var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, MessageBanner, Modal, ModalFooter, Select, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useIntl } from "react-intl";
import { defaultConfigFor, familyOf, } from "../../types/schedule";
import { applyScheduleChange, changeFrequency, fetchScheduleConfig, previewScheduleChange, } from "../../api/scheduleService";
import { EMPTY_VOLUMES, invalidateVolumeViews, usePeriodicalVolumes, } from "../../hooks/usePeriodicalVolumes";
import { usePeriodicalFrequencyOptions } from "../../hooks/usePeriodicalFrequencyOptions";
import { extractServerErrorMessage, readErrorCode, } from "../../utils/errorHandler";
import { invalidateDepositRecordViews } from "../../utils/depositRecordQueries";
import { findCurrentVolume, volumeOptionLabel } from "../../utils/volumeWindow";
import { ScheduleEditor } from "./ScheduleEditor";
import { PreviewModal } from "./PreviewModal";
import { ErrorType } from "../../types/ErrorType";
import css from "./ScheduleEditDialog.module.css";
var FrequencyResult = function (_a) {
    var _b, _c;
    var result = _a.result;
    var formatMessage = useIntl().formatMessage;
    var volume = (_b = result.volumeLabel) !== null && _b !== void 0 ? _b : "";
    var outcomeId = result.regenerated
        ? "ui-br-legaldeposits.schedule.dialog.frequency.result.regenerated"
        : "ui-br-legaldeposits.schedule.dialog.frequency.result.".concat((_c = result.notRegeneratedReason) !== null && _c !== void 0 ? _c : "UNCHANGED");
    return (React.createElement(MessageBanner, { type: result.regenerated ? "success" : "warning" },
        React.createElement("p", null, formatMessage({ id: outcomeId }, { volume: volume })),
        result.laterVolumesRegenerated.length > 0 && (React.createElement("p", null, formatMessage({
            id: "ui-br-legaldeposits.schedule.dialog.frequency.result.laterRegenerated",
        }, {
            count: result.laterVolumesRegenerated.length,
            volumes: result.laterVolumesRegenerated.join(", "),
        }))),
        result.laterVolumesKept.length > 0 && (React.createElement("p", null, formatMessage({
            id: "ui-br-legaldeposits.schedule.dialog.frequency.result.laterKept",
        }, {
            count: result.laterVolumesKept.length,
            volumes: result.laterVolumesKept.join(", "),
        })))));
};
export var ScheduleEditDialog = function (_a) {
    var open = _a.open, titleId = _a.titleId, periodicalLibraryId = _a.periodicalLibraryId, frequency = _a.frequency, onClose = _a.onClose;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useState(null), config = _b[0], setConfig = _b[1];
    var _c = useState(null), preview = _c[0], setPreview = _c[1];
    var _d = useState(false), previewStale = _d[0], setPreviewStale = _d[1];
    var _e = useState(null), dialogError = _e[0], setDialogError = _e[1];
    var _f = useState(null), applyError = _f[0], setApplyError = _f[1];
    var _g = useState(null), frequencyResult = _g[0], setFrequencyResult = _g[1];
    var _h = useState(null), chosenVolumeId = _h[0], setChosenVolumeId = _h[1];
    var _j = useState(frequency), selectedFrequency = _j[0], setSelectedFrequency = _j[1];
    var _k = useQuery(["schedule", titleId], function () { return fetchScheduleConfig(kyInstance, titleId); }, { enabled: open, staleTime: 0 }), data = _k.data, isLoading = _k.isLoading;
    var _l = usePeriodicalVolumes(periodicalLibraryId, open), volumesData = _l.data, volumesLoading = _l.isLoading;
    var volumes = volumesData !== null && volumesData !== void 0 ? volumesData : EMPTY_VOLUMES;
    var selectedVolume = useMemo(function () {
        var _a, _b;
        return (_b = (_a = volumes.find(function (volume) { return volume.id === chosenVolumeId; })) !== null && _a !== void 0 ? _a : findCurrentVolume(volumes)) !== null && _b !== void 0 ? _b : volumes[0];
    }, [volumes, chosenVolumeId]);
    useEffect(function () {
        var _a;
        if (!open) {
            setConfig(null);
            setPreview(null);
            setPreviewStale(false);
            setDialogError(null);
            setApplyError(null);
            setFrequencyResult(null);
            setChosenVolumeId(null);
            setSelectedFrequency(frequency);
            return;
        }
        if (data) {
            setConfig((_a = data.config) !== null && _a !== void 0 ? _a : defaultConfigFor(frequency));
        }
    }, [open, data, frequency]);
    var frequencyChanged = selectedFrequency !== frequency;
    var family = useMemo(function () { return familyOf(selectedFrequency); }, [selectedFrequency]);
    var isManual = family === "MANUAL";
    var frequencyOptions = usePeriodicalFrequencyOptions();
    var volumeOptions = useMemo(function () {
        return volumes.map(function (volume) { return ({
            value: volume.id,
            label: volumeOptionLabel(volume, formatMessage),
        }); });
    }, [volumes, formatMessage]);
    var errorFallback = formatMessage({
        id: "ui-br-legaldeposits.schedule.dialog.error",
    });
    var previewMutation = useMutation(function (payload) {
        return previewScheduleChange(kyInstance, titleId, payload);
    }, {
        onSuccess: function (result) {
            setDialogError(null);
            setPreview(result);
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        setPreview(null);
                        setPreviewStale(false);
                        _a = setDialogError;
                        return [4 /*yield*/, extractServerErrorMessage(error, formatMessage, errorFallback)];
                    case 1:
                        _a.apply(void 0, [_b.sent()]);
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var applyMutation = useMutation(function (payload) {
        return applyScheduleChange(kyInstance, titleId, payload);
    }, {
        onSuccess: function () {
            invalidateVolumeViews(queryClient);
            invalidateDepositRecordViews(queryClient);
            queryClient.invalidateQueries(["schedule", titleId]);
            setPreview(null);
            onClose();
        },
        onError: function (error, payload) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, readErrorCode(error)];
                    case 1:
                        if ((_b.sent()) === ErrorType.SCHEDULE_PLAN_STALE) {
                            setApplyError(null);
                            setPreviewStale(true);
                            previewMutation.mutate({
                                config: payload.config,
                                volumeId: payload.volumeId,
                            });
                            return [2 /*return*/];
                        }
                        _a = setApplyError;
                        return [4 /*yield*/, extractServerErrorMessage(error, formatMessage, errorFallback)];
                    case 2:
                        _a.apply(void 0, [_b.sent()]);
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var changeFrequencyMutation = useMutation(function (newFrequency) {
        return changeFrequency(kyInstance, titleId, newFrequency);
    }, {
        onSuccess: function (result) {
            setDialogError(null);
            setFrequencyResult(result);
            queryClient.invalidateQueries(["periodicalLibraries"]);
            queryClient.invalidateQueries(["periodical-library-detail"]);
            invalidateVolumeViews(queryClient);
            invalidateDepositRecordViews(queryClient);
            queryClient.invalidateQueries(["schedule", titleId]);
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = setDialogError;
                        return [4 /*yield*/, extractServerErrorMessage(error, formatMessage, errorFallback)];
                    case 1: return [2 /*return*/, _a.apply(void 0, [_b.sent()])];
                }
            });
        }); },
    });
    var handleFrequencyChange = useCallback(function (e) {
        var _a;
        var next = e.target.value;
        setDialogError(null);
        setSelectedFrequency(next);
        setConfig(next === frequency
            ? ((_a = data === null || data === void 0 ? void 0 : data.config) !== null && _a !== void 0 ? _a : defaultConfigFor(frequency))
            : defaultConfigFor(next));
    }, [frequency, data]);
    var handleVolumeChange = useCallback(function (e) {
        setDialogError(null);
        setChosenVolumeId(e.target.value);
    }, []);
    var handleSave = useCallback(function () {
        if (!selectedVolume)
            return;
        setPreviewStale(false);
        setApplyError(null);
        previewMutation.mutate({ config: config, volumeId: selectedVolume.id });
    }, [previewMutation, config, selectedVolume]);
    var handleSaveFrequency = useCallback(function () {
        if (selectedFrequency) {
            changeFrequencyMutation.mutate(selectedFrequency);
        }
    }, [selectedFrequency, changeFrequencyMutation]);
    var handleConfirm = useCallback(function () {
        if (!preview)
            return;
        setApplyError(null);
        applyMutation.mutate({
            config: config,
            volumeId: preview.volumeId,
            planToken: preview.planToken,
        });
    }, [applyMutation, config, preview]);
    var handleCancelPreview = useCallback(function () {
        setPreview(null);
        setPreviewStale(false);
        setApplyError(null);
    }, []);
    var saveDisabled = frequencyChanged
        ? changeFrequencyMutation.isLoading || !selectedFrequency
        : isLoading ||
            volumesLoading ||
            isManual ||
            !selectedVolume ||
            previewMutation.isLoading;
    var footer = frequencyResult ? (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: onClose }, formatMessage({ id: "ui-br-legaldeposits.common.close" })))) : (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: frequencyChanged ? handleSaveFrequency : handleSave, disabled: saveDisabled }, formatMessage({ id: "ui-br-legaldeposits.schedule.dialog.save" })),
        React.createElement(Button, { onClick: onClose, disabled: applyMutation.isLoading || changeFrequencyMutation.isLoading }, formatMessage({ id: "ui-br-legaldeposits.schedule.dialog.cancel" }))));
    var renderBody = function () {
        var _a;
        if (frequencyResult) {
            return React.createElement(FrequencyResult, { result: frequencyResult });
        }
        if (isLoading) {
            return React.createElement("p", null, formatMessage({ id: "ui-br-legaldeposits.loading" }));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Select, { label: formatMessage({
                    id: "ui-br-legaldeposits.schedule.dialog.frequency.label",
                }), dataOptions: frequencyOptions, value: selectedFrequency !== null && selectedFrequency !== void 0 ? selectedFrequency : "", onChange: handleFrequencyChange, disabled: previewMutation.isLoading || changeFrequencyMutation.isLoading }),
            frequencyChanged && (React.createElement("p", { className: css.changeNote }, formatMessage({
                id: "ui-br-legaldeposits.schedule.dialog.frequency.changeNote",
            }))),
            !frequencyChanged && !isManual && volumes.length > 0 && (React.createElement(Select, { label: formatMessage({
                    id: "ui-br-legaldeposits.schedule.dialog.volume",
                }), dataOptions: volumeOptions, value: (_a = selectedVolume === null || selectedVolume === void 0 ? void 0 : selectedVolume.id) !== null && _a !== void 0 ? _a : "", onChange: handleVolumeChange, disabled: previewMutation.isLoading, required: true })),
            !frequencyChanged &&
                !isManual &&
                !volumesLoading &&
                volumes.length === 0 && (React.createElement("p", { className: css.changeNote }, formatMessage({
                id: "ui-br-legaldeposits.schedule.dialog.noVolume",
            }))),
            !frequencyChanged &&
                (!isManual && config === null ? (React.createElement("p", null, formatMessage({ id: "ui-br-legaldeposits.loading" }))) : (React.createElement(ScheduleEditor, { frequency: selectedFrequency, value: config, onChange: setConfig, disabled: previewMutation.isLoading || applyMutation.isLoading }))),
            dialogError && (React.createElement(MessageBanner, { type: "error" }, dialogError))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { open: open, size: "medium", label: formatMessage({
                id: "ui-br-legaldeposits.schedule.dialog.title",
            }), footer: footer, onClose: onClose }, renderBody()),
        React.createElement(PreviewModal, { open: preview !== null, preview: preview, stale: previewStale, errorMessage: applyError, onConfirm: handleConfirm, onCancel: handleCancelPreview, isPending: applyMutation.isLoading || previewMutation.isLoading })));
};
